'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { X, Sparkles, Check } from 'lucide-react'
import { captureToAgentDraft } from '@/lib/agents/agent-actions'
import { useAppToast } from '@/components/app/AppToast'

const MAX_TEXT = 10000

interface QuickCaptureModalProps {
  open: boolean
  onClose: () => void
}

/**
 * Quick Capture - the daily on-ramp. Jot or paste anything; it is saved as a
 * private DRAFT agent in your vault (inactive) to refine later. Reuses the
 * `captureToAgentDraft` server action.
 */
export function QuickCaptureModal({ open, onClose }: QuickCaptureModalProps) {
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<{ slug: string } | null>(null)
  const [mounted, setMounted] = useState(false)
  const { toast } = useAppToast()

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (open) {
      setText('')
      setTitle('')
      setError(null)
      setSubmitting(false)
      setSaved(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open || !mounted) return null

  const charCount = text.length
  const isOverLimit = charCount > MAX_TEXT

  async function save() {
    if (!text.trim() || isOverLimit || submitting) return
    setSubmitting(true)
    setError(null)
    const res = await captureToAgentDraft({
      text: text.trim(),
      name: title.trim() ? title.trim() : undefined,
    })
    setSubmitting(false)
    if (!res.success) {
      setError(res.error)
    } else if (res.data) {
      setSaved({ slug: res.data.slug })
      toast('success', 'Saved to your vault as a draft')
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-capture-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 shadow-[var(--shadow-md)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2
            id="quick-capture-title"
            className="flex items-center gap-1.5 text-[var(--text-sm)] font-semibold text-[var(--color-text-primary)]"
          >
            <Sparkles className="h-4 w-4 text-[var(--color-brand-indigo)]" aria-hidden="true" />
            Quick capture
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {saved ? (
          <div className="py-2">
            <div className="mb-3 flex items-center gap-2 text-[var(--text-sm)] text-[var(--color-text-primary)]">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-success-bg,rgba(34,197,94,0.15))] text-[var(--color-success,#22c55e)]">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              Saved to your vault as a draft.
            </div>
            <p className="mb-4 text-[var(--text-xs)] text-[var(--color-text-muted)]">
              It is private and inactive. Refine it into a working agent whenever you are ready.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                href={`/agents/${saved.slug}`}
                onClick={onClose}
                className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-3 py-1.5 text-[var(--text-xs)] font-semibold text-white hover:opacity-90"
              >
                Refine now
              </Link>
              <button
                type="button"
                onClick={() => setSaved(null)}
                className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-3 py-1.5 text-[var(--text-xs)] font-semibold text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
              >
                Capture another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-2 py-1.5 text-[var(--text-xs)] font-semibold text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-3 text-[var(--text-xs)] text-[var(--color-text-muted)]">
              Jot or paste anything. We save it as a private agent draft in your vault so you can shape it later.
            </p>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={50}
              placeholder="Title (optional)"
              className="mb-2 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-[var(--text-sm)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
            />
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              autoFocus
              placeholder="Paste a prompt, an idea, a snippet of instructions..."
              className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-[var(--text-sm)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-indigo)]"
            />
            <div className="mt-1 flex items-center justify-between">
              <span className={`text-[11px] ${isOverLimit ? 'text-[var(--color-error,#dc2626)]' : 'text-[var(--color-text-muted)]'}`}>
                {charCount.toLocaleString()} / {MAX_TEXT.toLocaleString()}
              </span>
              {error && (
                <span className="text-[11px] text-[var(--color-error,#dc2626)]" role="alert">
                  {error}
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-[var(--text-xs)] font-semibold text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!text.trim() || isOverLimit || submitting}
                className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-1.5 text-[var(--text-xs)] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : 'Save draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
